import { FC, useState } from 'react';

import { Menu, MenuButton, MenuItem, MenuProvider } from '@ariakit/react';
import { Media } from '@media-viewer';
import { useAtomValue } from 'jotai';

import { VideoPayload } from '@data';
import { useTranslate } from '@i18n';
import { cameraSetupAtom, hasFullAnalysisAtom, hasVisualizationsAtom, showVisualizationsAtom } from '@store';
import { Box, CaretDownIcon, Heading3, Heading4, Text, ThreeDIcon, u } from '@ui';

import * as styles from './styles.css';

export const VIDEO_TYPE = {
    CURRENT: 'current',
    COMPARISON: 'comparison',
} as const;

type PlaybackCellProps = {
    type: (typeof VIDEO_TYPE)[keyof typeof VIDEO_TYPE];
    onChangeView?: ((view: number) => void) | null;
} & VideoPayload;

const PLAYBACK_MODE = {
    VIDEO: 'VIDEO',
    COMBINED: 'OVERLAY',
    THREE_D: 'THREE_D',
} as const;

type PlaybackMode = (typeof PLAYBACK_MODE)[keyof typeof PLAYBACK_MODE];

type ViewControlsProps = {
    onChangeView: (view: number) => void;
    cameraName: string;
};

const ViewControls = ({ onChangeView, cameraName }: ViewControlsProps) => {
    const t = useTranslate();
    const cameraSetup = useAtomValue(cameraSetupAtom);
    return (
        <MenuProvider>
            <MenuButton>
                <Box p={u(1)}>
                    <CaretDownIcon color="white" weight="bold" />
                </Box>
            </MenuButton>
            <Menu gutter={u(1)} shift={-u(1)}>
                <Box display="flex" flexDirection="column" gap={u(0.5)} className={styles.menu}>
                    {cameraSetup?.cameras
                        .filter((camera) => camera.view <= 4)
                        .map((camera) => (
                            <MenuItem key={camera.id}>
                                <Text
                                    variant="p"
                                    color={camera.name === cameraName ? 'brand' : 'inherit'}
                                    onClick={() => onChangeView?.(camera.view)}
                                >
                                    {t(`camera_angle.${camera.name}`)}
                                </Text>
                            </MenuItem>
                        ))}
                </Box>
            </Menu>
        </MenuProvider>
    );
};

export const PlaybackCell: FC<PlaybackCellProps> = ({ type, view, cameraName, url, onChangeView }) => {
    const t = useTranslate();
    const [playbackMode, setPlaybackMode] = useState<PlaybackMode>(PLAYBACK_MODE.VIDEO);
    const hasVisualizations = useAtomValue(hasVisualizationsAtom);
    const showVisualizations = useAtomValue(showVisualizationsAtom);
    const hasFullAnalysis = useAtomValue(hasFullAnalysisAtom);

    const overlay = (
        <Box
            p={u(1)}
            flexGrow={1}
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            alignItems="stretch"
        >
            <Box display="flex">
                <Box display="flex" className={styles.header} position={'relative'}>
                    <Box display="flex" gap={u(1)}>
                        {onChangeView && <ViewControls cameraName={cameraName} onChangeView={onChangeView} />}
                        <Box className={styles.headerGroup}>
                            <Heading4 color="inverse">{type}</Heading4>
                            <Heading3 color="inverse">{`${t(`camera_angle.${cameraName}`)}`}</Heading3>
                        </Box>
                    </Box>
                    {hasFullAnalysis && (
                        <Box display="flex" gap={u(1)} className={styles.buttonGroup}>
                            <ThreeDIcon
                                weight="fill"
                                size={u(2)}
                                color="white"
                                className={styles.button}
                                opacity={PLAYBACK_MODE.THREE_D === playbackMode ? 1 : 0.5}
                                onClick={() =>
                                    setPlaybackMode((prev) =>
                                        prev === PLAYBACK_MODE.THREE_D ? PLAYBACK_MODE.VIDEO : PLAYBACK_MODE.THREE_D,
                                    )
                                }
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );

    if (!hasFullAnalysis && type === 'current') {
        return <Media.Cell type="video" src={url} overlay={overlay} origin={type} />;
    }

    if (playbackMode === PLAYBACK_MODE.VIDEO) {
        if (hasVisualizations && showVisualizations)
            return <Media.Cell type="combined" src={url} overlay={overlay} origin={type} view={view} />;
        return <Media.Cell type="video" src={url} overlay={overlay} origin={type} />;
    }

    if (playbackMode === PLAYBACK_MODE.THREE_D)
        return <Media.Cell type="viewer3d" origin={type} overlay={overlay} view={view} />;

    return null;
};
